import { Box, OSKIcon, OSKThemeType, Spinner, Table, TableColumn, Text, Tooltip } from 'oskcomponents';
import { noop } from 'oskcore';
import React from 'react';
import { connect } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { DataProvider } from '~/redux';
import { ProgramReport } from '~/redux/modules/monitor/reports';
import { RootState } from '~/redux/store';
import { date_format, getProgramId } from '../../../utils';

type AssetReportsTableProps = {
    /** The currently selected asset */
    selectedAssetId?: number;
    /** List of all program reports */
    reports: Array<ProgramReport>;
    /** Flag to check if we're still loading */
    isLoading: boolean;
    /** Method to call when the Download button is clicked */
    onDownload: (file: string) => void;
};

const AssetReportsTable = ({ reports, selectedAssetId, isLoading, onDownload, ...props }: AssetReportsTableProps) => {
    const theme = useTheme() as OSKThemeType;
    const { programId } = useParams();

    const reportsForAsset = reports?.filter((report) => report.assets.includes(parseInt(`${selectedAssetId ?? 0}`)));

    const columns = React.useMemo<Array<TableColumn>>(
        () => [
            {
                Header: 'Report',
                accessor: 'name',
            },
            {
                Header: 'Date',
                accessor: 'date',
                Cell: ({ value }: any) => {
                    return date_format(value);
                },
            },
            {
                Header: 'File',
                accessor: 'uri',
                Cell: ({ value }: any) => {
                    return (
                        <Box>
                            <Tooltip label="Download" position="top">
                                <a
                                    style={{ color: theme.colors.primary.fg, paddingRight: '14px' }}
                                    href={value}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <OSKIcon code="download" />
                                </a>
                            </Tooltip>
                            <Tooltip label="Preview" position="top">
                                <Link to={`/program/${programId}/monitor/reports`}>
                                    <OSKIcon code="search" fill="white" />
                                </Link>
                            </Tooltip>
                        </Box>
                    );
                },
            },
        ],
        [],
    );

    return (
        <DataProvider programId={getProgramId()} features={['reports']}>
            {(function () {
                if (isLoading) {
                    return (
                        <Box center="all">
                            <Spinner />
                        </Box>
                    );
                }

                if (reportsForAsset.length < 1) {
                    return (
                        <Box grow center="all" style={{ alignSelf: 'center' }}>
                            <Text>No reports available</Text>
                        </Box>
                    );
                }

                return <Table {...props} columns={columns} data={reportsForAsset} />;
            })()}
        </DataProvider>
    );
};

export { AssetReportsTable };
export type { AssetReportsTableProps };

const mapStateToProps = (state: RootState, ownProps: Partial<AssetReportsTableProps>) => {
    return {
        isLoading: state.monitor.reports.isFetching,
        reports: state.monitor.reports.reports,
    };
};

export default connect(mapStateToProps, noop)(AssetReportsTable);
